// #19 — Add State requests in the admin dashboard. List + edit (status), modeled
// on the contract request resource. Agency-scoped server-side (filterByAgency).
import {
  Datagrid,
  DateField,
  Edit,
  FunctionField,
  List,
  ReferenceField,
  SearchInput,
  SimpleForm,
  TextField,
} from "react-admin";
import {
  SaveWithDeleteToolbar,
  useBulkActionButtons,
} from "../deleteControls";

export const AddStateRequestLabel = "Add State Requests";

const agentName = (r) =>
  `${r?.firstName || ""} ${r?.lastName || ""}`.trim() || r?.npn || "(agent)";
const carrierNames = (r) =>
  (r?.carriers || [])
    .map((c) => (c.carrier && typeof c.carrier === "object" ? c.carrier.value : c.carrier))
    .filter(Boolean)
    .join(", ");

// CSV export of the visible columns. fetchRelatedRecords resolves the agency
// ids to names (#96) the same way the contract request exporter does.
const exporter = async (records, fetchRelatedRecords) => {
  const agencies = await fetchRelatedRecords(records, "agency", "agency");
  const rows = records.map((r) => ({
    createdAt: r.createdAt,
    agent: agentName(r),
    npn: r.npn,
    agency: agencies[r.agency]?.name || "",
    states: (r.states || []).join(" / "),
    carriers: carrierNames(r),
    ace: r.specialistName,
  }));
  const header = Object.keys(rows[0] || { createdAt: "" });
  const csv = [
    header.join(","),
    ...rows.map((row) =>
      header.map((h) => `"${String(row[h] ?? "").replace(/"/g, '""')}"`).join(",")
    ),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "add-state-requests.csv";
  a.click();
};

// #84 - free-text search: partial match on the agent's identity, the requested
// states, and the routed ACE's name.
const addStateFilters = [
  <SearchInput source="q" placeholder="Name, NPN, or state" alwaysOn />,
];

export const AddStateRequestList = (props) => (
  <List
    {...props}
    perPage={25}
    exporter={exporter}
    sort={{ field: "createdAt", order: "DESC" }}
    filters={addStateFilters}
  >
    <Datagrid rowClick="edit" bulkActionButtons={useBulkActionButtons()}>
      <DateField source="createdAt" label="Submitted" locale="en-US" options="short" />
      <FunctionField label="Agent" render={agentName} />
      <TextField source="npn" label="NPN" />
      {/* #96 - the requesting agent's agency, for every role: a principal with
          a downline has requests from several agencies in this list. */}
      <ReferenceField label="Agency" source="agency" reference="agency" emptyText="—">
        <TextField source="name" />
      </ReferenceField>
      <FunctionField label="States" render={(r) => (r.states || []).join(", ")} />
      <FunctionField label="Carriers" render={carrierNames} />
      <TextField source="specialistName" label="ACE" />
      {/* #57 - white glove: who prepared the request, when a principal did. */}
      <TextField source="preparedByName" label="Prepared by" emptyText="—" />
    </Datagrid>
  </List>
);

export const AddStateRequestEdit = (props) => (
  <Edit {...props}>
    <SimpleForm toolbar={<SaveWithDeleteToolbar />}>
      <FunctionField label="Agent" render={agentName} />
      <TextField source="npn" label="NPN" />
      <TextField source="email" label="E-Mail" />
      <TextField source="phone" label="Phone" />
      <FunctionField label="States to add" render={(r) => (r.states || []).join(", ")} />
      <FunctionField label="Carriers" render={carrierNames} />
      <TextField source="specialistName" label="Assigned ACE" />
      {/* #57 - white glove: set when a principal prepared this request. */}
      <FunctionField
        label="Prepared by"
        render={(r) =>
          r?.preparedByName ? `${r.preparedByName} (agency principal)` : "—"
        }
      />
    </SimpleForm>
  </Edit>
);
